import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LABELS } from "@match/shared";
import { ApproximateNote } from "@/components/session";
import { Button, Dash, ErrorNote, Field, SidePanel, inputClass, useCurrentUser } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { date, money, signClass } from "@/lib/format";
import { keys } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { Account, ClientDetail, LedgerRow, TransactionKind } from "@/lib/types";
import type { LedgerChange } from "@/lib/api";

/*
 * Registro movimenti del cliente e riconciliazione dei conti.
 *
 * Ogni riga si MODIFICA e si ELIMINA (solo ADMIN, motivo obbligatorio, audit
 * col prima e il dopo). Lo storno non c'è più: correggeva una riga
 * aggiungendone un'altra, e per leggere il registro bisognava tenere a mente
 * quali righe si annullavano fra loro.
 *
 * Prima di confermare si vede l'EFFETTO SUL CICLO, chiesto al backend con la
 * stessa funzione che poi scriverà: non una stima.
 *
 * L'importo è nella valuta del movimento; accanto, il valore in reporting col
 * tasso congelato e la sua fonte, marcato come stima quando è il riferimento
 * BCE.
 */

export const KIND_LABEL: Record<TransactionKind, string> = {
  DEPOSIT: "Versamento",
  WITHDRAWAL: "Prelievo",
  PROP_FEE: "Fee prop",
  PROP_FEE_REFUND: "Rimborso fee",
  PAYOUT_RECEIVED: "Payout ricevuto",
  MANAGER_COMMISSION_DUE: "Commissione maturata",
  MANAGER_COMMISSION_PAID: "Commissione pagata",
  PROFIT_SPLIT_CLIENT: "Quota cliente",
  PROFIT_SPLIT_MANAGER: "Quota gestore",
  ADJUSTMENT: "Rettifica",
};

/** Quota di uno split: le due righe si muovono insieme, e la somma non cambia. */
const isSplit = (k: string) => k === "PROFIT_SPLIT_CLIENT" || k === "PROFIT_SPLIT_MANAGER";

/** Segno imposto dal tipo: l'operatore scrive l'importo, il segno lo mette il form. */
const SIGN: Partial<Record<TransactionKind, 1 | -1>> = {
  DEPOSIT: 1,
  WITHDRAWAL: -1,
  PROP_FEE: -1,
  PROP_FEE_REFUND: 1,
  MANAGER_COMMISSION_DUE: -1,
  MANAGER_COMMISSION_PAID: 1,
};

const ACCOUNT_KINDS: TransactionKind[] = ["DEPOSIT", "WITHDRAWAL"];
const CYCLE_KINDS: TransactionKind[] = ["PROP_FEE", "PROP_FEE_REFUND", "MANAGER_COMMISSION_DUE", "MANAGER_COMMISSION_PAID"];
/** Il payout ricevuto nasce dal payout segnato come pagato, non dal form. */
/**
 * Quello che si registra A MANO. Fuori restano il payout — che nasce dal
 * payout segnato come pagato — e le due quote di uno split, che nascono da
 * quello stesso payout o da una bruciatura. Nel filtro invece ci sono tutti:
 * cercare le quote di un ciclo è una cosa che si fa.
 */
const FORM_KINDS = (Object.keys(KIND_LABEL) as TransactionKind[]).filter((k) => k !== "PAYOUT_RECEIVED" && !isSplit(k));

const FX_SOURCE_LABEL = { IDENTITY: "stessa valuta", MANUAL: "a mano", BROKER_FEED: "feed broker", ECB: "BCE" } as const;

/** Chi scrive nel registro: come il backend, anche CONTABILE. */
export function useCanWriteLedger(): boolean {
  const role = useCurrentUser().role;
  return role === "ADMIN" || role === "OPERATORE" || role === "CONTABILE";
}

export function ClientLedger({ client }: { client: ClientDetail }) {
  const canWrite = useCanWriteLedger();
  const [kind, setKind] = useState<TransactionKind | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [panel, setPanel] = useState(false);

  const q = { kind: kind || undefined, from: from || undefined, to: to ? `${to}T23:59:59.999Z` : undefined };
  const ledger = useQuery({ queryKey: [...keys.ledger(client.id), q], queryFn: () => api.ledger.list(client.id, q) });
  const reporting = ledger.data?.totals.reportingCurrency ?? "EUR";

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="mr-auto text-sm font-medium text-ink">Registro movimenti</h2>
        <FilterField label="Tipo">
          {(id) => (
            <select id={id} value={kind} onChange={(e) => setKind(e.target.value as TransactionKind | "")} className={`${inputClass} w-48 py-1.5`}>
              <option value="">Tutti</option>
              {(Object.keys(KIND_LABEL) as TransactionKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </FilterField>
        <FilterField label="Dal">{(id) => <input id={id} type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputClass} py-1.5`} />}</FilterField>
        <FilterField label="Al">{(id) => <input id={id} type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${inputClass} py-1.5`} />}</FilterField>
        {canWrite && (
          <Button variant="primary" onClick={() => setPanel(true)}>
            Nuovo movimento
          </Button>
        )}
      </div>

      {ledger.error ? (
        <ErrorNote>{errorMessage(ledger.error)}</ErrorNote>
      ) : !ledger.data ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : ledger.data.rows.length === 0 ? (
        <p className="rounded-card border border-dashed border-line px-4 py-6 text-sm text-muted">
          {kind || from || to
            ? "Nessun movimento con questi filtri."
            : "Nessun movimento. Registra versamenti e prelievi del conto broker, le fee pagate alla prop e le commissioni: servono alla riconciliazione e ai totali del ciclo."}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs text-faint">
                  <th className="py-2 pr-4 font-medium">Data</th>
                  <th className="py-2 pr-4 font-medium">Tipo</th>
                  <th className="py-2 pr-4 font-medium">Conto o ciclo</th>
                  <th className="py-2 pr-4 text-right font-medium">Importo</th>
                  <th className="py-2 pr-4 text-right font-medium">In {reporting}</th>
                  <th className="py-2 pr-4 font-medium">Riferimento</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {ledger.data.rows.map((r) => (
                  <LedgerLine key={r.id} row={r} sibling={gemellaDi(r, ledger.data!.rows)} clientId={client.id} canWrite={canWrite} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted">
            Saldo del registro{" "}
            {Object.entries(ledger.data.totals.byCurrency).map(([cur, v], i) => (
              <span key={cur}>
                {i > 0 && " · "}
                <span className={signClass(v)}>{money(v, cur)}</span>
              </span>
            ))}
            {" · "}in {reporting} <span className={signClass(ledger.data.totals.reporting)}>{money(ledger.data.totals.reporting, reporting)}</span>
            {ledger.data.totals.fxAssumed && <ApproximateNote title="Almeno un movimento è convertito col riferimento BCE, non col cambio del conto." />}
          </p>
        </>
      )}

      <TransactionPanel open={panel} client={client} reportingCurrency={reporting} onClose={() => setPanel(false)} />
    </section>
  );
}

function FilterField({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  return (
    <div className="[&_label]:mb-1 [&_label]:text-xs">
      <Field label={label}>{children}</Field>
    </div>
  );
}

/**
 * L'altra quota dello stesso split, cercata nelle righe gia' caricate con la
 * STESSA regola del backend: stesso ciclo, tipo opposto, stesso istante. Due
 * regole diverse vorrebbero dire un form che propone una coppia e un
 * salvataggio che ne corregge un'altra.
 */
function gemellaDi(r: LedgerRow, righe: LedgerRow[]): LedgerRow | null {
  // Senza ciclo non è una quota di split: due righe con `cycleId` nullo non
  // sono una coppia, e cercarle per istante le accoppierebbe per sbaglio.
  if (!isSplit(r.kind) || !r.cycleId) return null;
  const opposto = r.kind === "PROFIT_SPLIT_CLIENT" ? "PROFIT_SPLIT_MANAGER" : "PROFIT_SPLIT_CLIENT";
  return righe.find((x) => x.kind === opposto && x.cycleId === r.cycleId && x.occurredAt === r.occurredAt) ?? null;
}

function LedgerLine({ row: r, sibling, clientId, canWrite }: { row: LedgerRow; sibling: LedgerRow | null; clientId: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const isAdmin = useCurrentUser().role === "ADMIN";
  /** Quale modulo è aperto su questa riga: nessuno, la modifica, l'eliminazione. */
  const [gesto, setGesto] = useState<"MODIFICA" | "ELIMINA" | null>(null);
  const [reason, setReason] = useState("");
  const [amountNuovo, setAmountNuovo] = useState("");
  /** Le due quote di uno split, positive come le legge chi guarda. */
  const [quote, setQuote] = useState({ client: "", manager: "" });
  const [effetto, setEffetto] = useState<LedgerChange | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = Number(r.amount);
  /*
   * Solo ADMIN, e non su una riga di payout: quella è l'incasso registrato dal
   * payout, e si corregge da lì.
   *
   * Una quota di split si modifica solo INSIEME all'altra, quindi serve
   * averla: senza (una riga sola rimasta, o un filtro che nasconde la gemella)
   * il modulo non si apre, invece di aprirsi e farsi rifiutare dopo.
   */
  const coppiaSplit = isSplit(r.kind) ? sibling : null;
  const modificabile = canWrite && isAdmin && !r.payoutId && (!isSplit(r.kind) || !!coppiaSplit);
  /** Una quota sola non si elimina: le due stanno insieme (il backend lo rifiuta). */
  const eliminabile = modificabile && !isSplit(r.kind);
  /** Il quoziente da rispettare: quello che le due quote sommano ADESSO. */
  const nettoSplit = coppiaSplit ? Math.abs(amount) + Math.abs(Number(coppiaSplit.amount)) : 0;

  const aggiorna = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.ledger(clientId) }),
      qc.invalidateQueries({ queryKey: ["reconciliation"] }),
      qc.invalidateQueries({ queryKey: keys.client(clientId) }),
    ]);

  const apri = async (quale: "MODIFICA" | "ELIMINA") => {
    setGesto(quale);
    setReason("");
    setError(null);
    setAmountNuovo(quale === "MODIFICA" ? String(Math.abs(amount)) : "");
    if (coppiaSplit) {
      const cliente = r.kind === "PROFIT_SPLIT_CLIENT" ? r : coppiaSplit;
      const gestore = r.kind === "PROFIT_SPLIT_CLIENT" ? coppiaSplit : r;
      setQuote({ client: String(Math.abs(Number(cliente.amount))), manager: String(Math.abs(Number(gestore.amount))) });
    }
    setEffetto(null);
    // L'effetto sul ciclo si chiede subito per l'eliminazione, che non ha
    // campi da compilare: per la modifica si chiede quando l'importo cambia.
    if (quale === "ELIMINA") await anteprima({ remove: true });
  };

  const anteprima = async (body: { remove?: boolean; amount?: string; split?: { client: string; manager: string } }) => {
    try {
      setEffetto(await api.ledger.changePreview(r.id, { reason: "anteprima", ...body }));
      setError(null);
    } catch (e) {
      setEffetto(null);
      setError(errorMessage(e));
    }
  };

  const conferma = async () => {
    if (reason.trim().length < 3) return setError("Scrivi il motivo");
    setBusy(true);
    setError(null);
    try {
      if (gesto === "ELIMINA") await api.ledger.remove(r.id, reason.trim());
      else if (coppiaSplit) await api.ledger.update(r.id, { reason: reason.trim(), split: quote });
      else await api.ledger.update(r.id, { reason: reason.trim(), amount: importoConSegno(amountNuovo) });
      await aggiorna();
      setGesto(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Il segno lo tiene la riga: l'operatore scrive l'importo come lo legge. */
  const importoConSegno = (v: string) => (amount < 0 ? `-${v.replace("-", "")}` : v.replace("-", ""));

  return (
    <tr className={`border-b border-line/60 align-top ${r.reversedBy ? "text-muted" : ""}`}>
      <td className="py-2.5 pr-4 text-muted">{date(r.occurredAt)}</td>
      <td className="py-2.5 pr-4">
        {KIND_LABEL[r.kind]}
        {r.reversesId && <span className="block text-xs text-faint">storno</span>}
        {r.reversedBy && <span className="block text-xs text-faint">stornato il {date(r.reversedBy.occurredAt)}</span>}
      </td>
      <td className="py-2.5 pr-4">
        {r.account ? (
          <span className={r.account.role === "PROP" ? "text-prop" : r.account.role === "BROKER" ? "text-broker" : ""}>{r.account.label}</span>
        ) : r.cycle ? (
          <span>{r.cycle.label ?? LABELS.cyclePhase[r.cycle.currentPhase]}</span>
        ) : (
          <Dash />
        )}
      </td>
      <td className={`py-2.5 pr-4 text-right ${signClass(amount)}`}>{money(amount, r.currency)}</td>
      <td className="py-2.5 pr-4 text-right">
        <span className={signClass(Number(r.amountReporting))}>
          {r.fxAssumed ? "≈ " : ""}
          {money(r.amountReporting, r.reportingCurrency)}
        </span>
        <span className="block text-xs text-faint" title={r.fxAssumed ? "Riferimento BCE, non il cambio del conto" : undefined}>
          {r.fxSource === "IDENTITY" ? FX_SOURCE_LABEL.IDENTITY : `${Number(r.fxRate)} · ${FX_SOURCE_LABEL[r.fxSource]}`}
        </span>
      </td>
      <td className="py-2.5 pr-4 text-muted">
        {r.reference ?? <Dash />}
        {r.notes && <span className="block text-xs text-faint">{r.notes}</span>}
      </td>
      <td className="py-2.5 text-right">
        {gesto ? (
          <div className="ml-auto w-64 space-y-1.5 text-left">
            {gesto === "MODIFICA" &&
              (coppiaSplit ? (
                /*
                 * LE DUE QUOTE INSIEME, e la somma da rispettare scritta
                 * accanto: le righe nascono insieme da un importo, e
                 * correggerne una sola vorrebbe dire uno split che non copre
                 * piu' quell'importo. Qui si sposta la riga fra cliente e
                 * gestore; per cambiare quanto c'era da dividere si rifa'
                 * l'operazione che le ha generate.
                 */
                <div className="space-y-1">
                  <p className="text-xs text-faint">
                    Somma da rispettare <span className="text-muted">{money(nettoSplit, r.currency)}</span>
                  </p>
                  <label className="block text-xs text-muted">
                    Quota cliente
                    <input
                      inputMode="decimal"
                      value={quote.client}
                      onChange={(e) => setQuote((q) => ({ ...q, client: decimalInput(e.target.value) ?? q.client }))}
                      onBlur={() => void anteprima({ split: quote })}
                      className={`${inputClass} py-1 text-right`}
                    />
                  </label>
                  <label className="block text-xs text-muted">
                    Quota gestore
                    <input
                      inputMode="decimal"
                      value={quote.manager}
                      onChange={(e) => setQuote((q) => ({ ...q, manager: decimalInput(e.target.value) ?? q.manager }))}
                      onBlur={() => void anteprima({ split: quote })}
                      className={`${inputClass} py-1 text-right`}
                    />
                  </label>
                </div>
              ) : (
                <label className="block text-xs text-muted">
                  Importo
                  <input
                    inputMode="decimal"
                    value={amountNuovo}
                    onChange={(e) => setAmountNuovo((prec) => decimalInput(e.target.value) ?? prec)}
                    onBlur={() => void anteprima({ amount: importoConSegno(amountNuovo) })}
                    className={`${inputClass} py-1 text-right`}
                  />
                </label>
              ))}
            <input
              aria-label={gesto === "ELIMINA" ? "Motivo dell'eliminazione" : "Motivo della modifica"}
              placeholder="Motivo (obbligatorio)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={`${inputClass} py-1`}
            />
            {/*
              * L'EFFETTO SUL CICLO PRIMA DI CONFERMARE, e non una stima: sono i
              * numeri che il backend scriverà, calcolati dalla stessa funzione.
              */}
            {effetto?.before && effetto.after && (
              <dl className="space-y-0.5 rounded border border-line bg-raised px-2 py-1 text-xs">
                <EffettoRiga label="Fee pagate" prima={effetto.before.feesPaid} dopo={effetto.after.feesPaid} valuta={effetto.after.currency} />
                <EffettoRiga label="Payout" prima={effetto.before.payoutsReceived} dopo={effetto.after.payoutsReceived} valuta={effetto.after.currency} />
                <EffettoRiga label="Da recuperare" prima={effetto.before.recoverAmount} dopo={effetto.after.recoverAmount} valuta={effetto.after.currency} />
              </dl>
            )}
            {effetto && !effetto.cycleId && <p className="text-xs text-faint">Questa riga non è di un ciclo: non ci sono numeri da rifare.</p>}
            {error && <p className="text-xs text-loss">{error}</p>}
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => setGesto(null)}>
                Annulla
              </Button>
              <Button size="sm" variant={gesto === "ELIMINA" ? "danger" : "primary"} disabled={busy} onClick={() => void conferma()}>
                {busy ? "..." : gesto === "ELIMINA" ? "Conferma eliminazione" : "Salva"}
              </Button>
            </div>
          </div>
        ) : (
          modificabile && (
            <span className="inline-flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => void apri("MODIFICA")}>
                Modifica
              </Button>
              {eliminabile && (
                <Button size="sm" variant="ghost" onClick={() => void apri("ELIMINA")}>
                  Elimina
                </Button>
              )}
            </span>
          )
        )}
      </td>
    </tr>
  );
}

/** Una riga del prima/dopo: si mostra anche quando non cambia, o «non cambia» sarebbe un'assenza. */
function EffettoRiga({ label, prima, dopo, valuta }: { label: string; prima: number; dopo: number; valuta: string }) {
  const uguale = Math.abs(prima - dopo) < 0.005;
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-faint">{label}</dt>
      <dd className={uguale ? "text-muted" : "text-ink"}>
        {money(prima, valuta)}
        {!uguale && <span className="text-ink"> → {money(dopo, valuta)}</span>}
      </dd>
    </div>
  );
}

function TransactionPanel({ open, client, reportingCurrency, onClose }: { open: boolean; client: ClientDetail; reportingCurrency: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<TransactionKind>("DEPOSIT");
  const [amount, setAmount] = useState("");
  const [negative, setNegative] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [cycleId, setCycleId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [occurredAt, setOccurredAt] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsAccount = ACCOUNT_KINDS.includes(kind);
  const needsCycle = CYCLE_KINDS.includes(kind);
  const account = client.accounts.find((a) => a.id === accountId);
  const effectiveCurrency = account?.currency ?? currency;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAccountId(client.accounts.find((a) => a.role === "BROKER")?.id ?? "");
    setCycleId(client.cycles[0]?.id ?? "");
  }, [open, client]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = decimalInput(amount.replace(/^-/, ""));
    if (!v || Number(v) === 0) return setError("Scrivi un importo diverso da zero");
    if (needsAccount && !accountId) return setError("Scegli il conto");
    if (needsCycle && !cycleId) return setError("Scegli il ciclo");
    if (kind === "ADJUSTMENT" && !notes.trim()) return setError("Una rettifica richiede il motivo");
    const rate = fxRate.trim() ? decimalInput(fxRate) : null;
    if (fxRate.trim() && !rate) return setError("Il tasso deve essere un numero");
    const sign = SIGN[kind] ?? (negative ? -1 : 1);

    setBusy(true);
    try {
      await api.ledger.create({
        clientId: client.id,
        kind,
        amount: sign < 0 ? `-${v}` : v,
        currency: needsAccount || accountId ? undefined : currency,
        accountId: needsAccount ? accountId : null,
        cycleId: needsCycle ? cycleId : null,
        occurredAt: new Date(`${occurredAt}T12:00:00Z`).toISOString(),
        reference: reference.trim() || null,
        notes: notes.trim() || null,
        ...(rate ? { fxRate: rate } : {}),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: keys.ledger(client.id) }), qc.invalidateQueries({ queryKey: ["reconciliation"] }), qc.invalidateQueries({ queryKey: keys.client(client.id) })]);
      setAmount("");
      setReference("");
      setNotes("");
      setFxRate("");
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const askRate = effectiveCurrency !== reportingCurrency || /Tasso .* non disponibile/.test(error ?? "");

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Nuovo movimento"
      subtitle={client.name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="ledger-form" disabled={busy}>
            {busy ? "Registrazione…" : "Registra"}
          </Button>
        </>
      }
    >
      <form id="ledger-form" onSubmit={submit} className="space-y-4">
        <p className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">Una riga registrata non si modifica: se è sbagliata si storna e se ne registra un'altra.</p>
        <Field label="Tipo">
          {(id) => (
            <select id={id} value={kind} onChange={(e) => setKind(e.target.value as TransactionKind)} className={inputClass}>
              {FORM_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </Field>
        {needsAccount && (
          <Field label="Conto" hint="La valuta del movimento è quella del conto.">
            {(id) => (
              <select id={id} value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  Scegli il conto
                </option>
                {client.accounts.map((a: Account) => (
                  <option key={a.id} value={a.id}>
                    {a.label} · {a.currency}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {needsCycle && (
          <Field label="Ciclo">
            {(id) => (
              <select id={id} value={cycleId} onChange={(e) => setCycleId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  Scegli il ciclo
                </option>
                {client.cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label ?? `${c.propFirm.name} ${c.preset.name}`} · {LABELS.cyclePhase[c.currentPhase]}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Importo" hint={SIGN[kind] ? (SIGN[kind]! < 0 ? "Registrato in uscita." : "Registrato in entrata.") : undefined}>
            {(id) => <input id={id} inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} className={inputClass} />}
          </Field>
          {needsAccount ? (
            <Field label="Valuta">{(id) => <input id={id} disabled value={account?.currency ?? ""} className={inputClass} />}</Field>
          ) : (
            <Field label="Valuta" hint="Quella in cui il movimento avviene o si calcola.">
              {(id) => <input id={id} value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={inputClass} />}
            </Field>
          )}
        </div>
        {!SIGN[kind] && (
          <label className="flex items-center gap-2 text-sm text-muted">
            <input type="checkbox" checked={negative} onChange={(e) => setNegative(e.target.checked)} /> Rettifica in uscita (negativa)
          </label>
        )}
        <Field label="Data">{(id) => <input id={id} type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} className={inputClass} />}</Field>
        {askRate && (
          <Field label={`Tasso ${effectiveCurrency}/${reportingCurrency}`} hint="Vuoto = feed del broker, poi BCE. Si scrive se nessuno dei due è disponibile: si congela sul movimento.">
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 0.9200" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        <Field label="Riferimento" hint="Bonifico, hash, id del payout sulla piattaforma della prop.">
          {(id) => <input id={id} value={reference} onChange={(e) => setReference(e.target.value)} className={inputClass} />}
        </Field>
        <Field label={kind === "ADJUSTMENT" ? "Motivo" : "Note"}>
          {(id) => <textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
